// @givmo/org-console — MEASUREMENT: the three ids, their shapes, and the tristate the PUT rides on.
//
// ══ THE RULES LIVE HERE, THE CARD ONLY RENDERS THEM ════════════════════════════════════════════
//
// Nothing in this file touches React or the network. The card is then a thin rendering of decisions
// made here, and the two decisions that are easy to get silently wrong — which id shapes are
// acceptable, and which keys a save is allowed to put on the wire — are unit-testable without a DOM.
//
// ⚠ THE THREE PATTERNS MIRROR THE SERVER'S, WHICH IS THE AUTHORITY. They are copied from
// `app/domain/org_measurement_config.py` character for character so an inline message can appear as
// someone types, and the server re-applies its own before anything is stored. Because the two rules
// are IDENTICAL there is no input this file refuses that the server would have taken — a client-side
// gate that is merely SIMILAR would silently refuse valid ids and read as a broken form.
//
// ⚠ THEY ARE ANCHORED AND BOUNDED IN BOTH DIRECTIONS, and both halves are load-bearing. Unanchored,
// `G-ABC123` embedded in a script fragment would match; unbounded, an arbitrarily long string would
// be stored, echoed back into this editor, and later interpolated into a third-party script URL by
// whatever module reads this configuration. The tight character classes are a boundary, not tidiness.
//
// ⚠ NORMALIZATION HAPPENS BEFORE VALIDATION, exactly as it does on the server: trim, then upper-case,
// then match. So `  gtm-w5x9zz  ` is a VALID entry that stores as `GTM-W5X9ZZ`, and the lower-cased
// prefix — the ordinary paste error — is absorbed rather than refused.
//
// ⚠ AN ID IS STORED AND WELL-FORMED, AND THE PRODUCT PROMISES NOTHING MORE. No call is made to the
// analytics provider at save time, on either side of the wire, so a perfectly-shaped id for a
// property that does not exist is accepted like any other. The card's copy says so.

/** The three configurable ids, in wire order. The ONE enumeration this package reads. */
export const MEASUREMENT_FIELDS = [
  'ga4_measurement_id',
  'meta_pixel_id',
  'gtm_container_id',
] as const;

/** One of the three ids. */
export type MeasurementField = (typeof MEASUREMENT_FIELDS)[number];

/** What one input needs to render and to judge itself. */
export interface MeasurementFieldSpec {
  field: MeasurementField;
  /** The input's label. The vendor's own name for the id, so it is findable in their console. */
  label: string;
  /** A shaped example, shown in the empty input. Never a substitute for the label. */
  example: string;
  /**
   * The shape, in words.
   *
   * ⚠ SPELLED, NOT DERIVED FROM THE PATTERN. Handing someone a regular expression to reverse-engineer
   * is not an explanation, and this string doubles as the inline message when an entry does not
   * match — so it must read as an instruction on its own.
   */
  expected: string;
  /** The server's own pattern, applied to the NORMALIZED value. */
  pattern: RegExp;
}

/**
 * The three fields as the card renders them.
 *
 * `expected` tracks the server's `_EXPECTED` map so the message someone reads inline is the message
 * the 422 would have carried — the two must not drift into saying different things about one rule.
 */
export const MEASUREMENT_FIELD_SPECS: readonly MeasurementFieldSpec[] = [
  {
    field: 'ga4_measurement_id',
    label: 'Google Analytics measurement ID',
    example: 'G-XXXXXXXXXX',
    expected: 'a GA4 measurement ID such as G-XXXXXXXXXX',
    pattern: /^G-[A-Z0-9]{4,14}$/,
  },
  {
    field: 'meta_pixel_id',
    label: 'Meta pixel ID',
    example: '1234567890',
    expected: 'a Meta pixel ID of 5-20 digits',
    pattern: /^[0-9]{5,20}$/,
  },
  {
    field: 'gtm_container_id',
    label: 'Google Tag Manager container ID',
    example: 'GTM-XXXXXXX',
    expected: 'a GTM container ID such as GTM-XXXXXXX',
    pattern: /^GTM-[A-Z0-9]{4,10}$/,
  },
];

/** One field's spec, by name. */
export function measurementFieldSpec(field: string): MeasurementFieldSpec | null {
  return MEASUREMENT_FIELD_SPECS.find((spec) => spec.field === field) ?? null;
}

/**
 * A readable name for one of the three fields — for the activity record, which stores the wire key.
 *
 * An unrecognized key renders as ITSELF rather than as nothing: the backend's field list can grow a
 * member before this package ships copy for it, and a dated row naming `something_new` is still
 * evidence while a blank one is not.
 */
export function measurementFieldLabel(field: string): string {
  return measurementFieldSpec(field)?.label ?? field;
}

/** Trim and upper-case — the server's normalization, applied before anything else looks at a value. */
export function normalizeMeasurementId(raw: string): string {
  return raw.trim().toUpperCase();
}

/**
 * Whether a NON-EMPTY entry is well-formed.
 *
 * ⚠ THE EMPTY STRING IS NOT ANSWERED HERE, and deliberately: empty is not an invalid id, it is the
 * absence of one — which is how someone clears a field. Callers ask this only about entries that
 * have something in them; {@link invalidMeasurementFields} is the one that knows the difference.
 */
export function isValidMeasurementId(field: string, raw: string): boolean {
  const spec = measurementFieldSpec(field);
  if (spec === null) return false;
  return spec.pattern.test(normalizeMeasurementId(raw));
}

/** What the three inputs hold, as typed. Raw text — never normalized in place under someone's cursor. */
export type MeasurementDraft = Record<MeasurementField, string>;

/** The stored configuration, reduced to the three ids. */
export type MeasurementValues = Partial<Record<MeasurementField, string | null>>;

/** An empty draft — every field blank. The shape an organization with no stored row renders as. */
export function emptyMeasurementDraft(): MeasurementDraft {
  return { ga4_measurement_id: '', meta_pixel_id: '', gtm_container_id: '' };
}

/**
 * The draft an organization's stored configuration opens as.
 *
 * A null id becomes an EMPTY input, which is the same thing said two ways — the store's own contract
 * is that an all-null row and no row at all are one state, so there is nothing here to distinguish.
 */
export function measurementDraftFrom(values: MeasurementValues | null): MeasurementDraft {
  const draft = emptyMeasurementDraft();
  if (values === null) return draft;
  for (const field of MEASUREMENT_FIELDS) draft[field] = values[field] ?? '';
  return draft;
}

/** Every field whose entry is non-empty and does not match its shape. */
export function invalidMeasurementFields(draft: MeasurementDraft): MeasurementField[] {
  return MEASUREMENT_FIELDS.filter(
    (field) => draft[field].trim().length > 0 && !isValidMeasurementId(field, draft[field]),
  );
}

/**
 * The PATCH BODY — the tristate, and the one place it is decided.
 *
 * Three outcomes per field, and the difference between the first two is a difference in the JSON
 * ITSELF rather than in a value:
 *
 *   * the entry matches what is stored          → the key is ABSENT, and the server leaves it alone;
 *   * the entry is empty and something IS stored → the key is present as `null`, which CLEARS it;
 *   * the entry differs                          → the key carries the normalized id.
 *
 * ⚠ AN ABSENT KEY AND A `null` KEY ARE OPPOSITE INSTRUCTIONS, so a field is never assigned
 * `undefined` here — `JSON.stringify` would drop it, but only by accident, and a future caller
 * reading this object directly would see a key that means "unchanged" sitting beside one that means
 * "clear". Only changed fields are ever written into the result.
 *
 * ⚠ SERIALIZING ALL THREE FROM FORM STATE WOULD BE A DATA-LOSS BUG, not a verbose request. A field
 * someone never touched, emitted as `null` because its input happens to be empty, clears an id that
 * was working — which is exactly what this function exists to make impossible.
 *
 * An empty entry for a field that is already unset yields no key at all: there is nothing to clear,
 * and the save is then honestly a no-op rather than a write that appears in the activity record.
 */
export function buildMeasurementPatch(
  draft: MeasurementDraft,
  stored: MeasurementValues | null,
): MeasurementValues {
  const patch: MeasurementValues = {};
  for (const field of MEASUREMENT_FIELDS) {
    const next = normalizeMeasurementId(draft[field]);
    const current = stored?.[field] ?? null;
    if (next.length === 0) {
      if (current !== null) patch[field] = null;
      continue;
    }
    if (next !== current) patch[field] = next;
  }
  return patch;
}

/** Whether a patch would write anything at all. */
export function measurementPatchIsEmpty(patch: MeasurementValues): boolean {
  return Object.keys(patch).length === 0;
}
